import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createForm,
  deleteForm,
  getForm,
  listForms,
  listSubmissions,
  updateForm,
  type FormInput,
} from '@/api/forms'

export function useForms() {
  return useQuery({ queryKey: ['forms'], queryFn: () => listForms() })
}

/** All forms, only fetched when allowed (forms are admin-only; an agent would get a 403). */
export function useFormsIf(enabled: boolean) {
  return useQuery({ queryKey: ['forms'], queryFn: () => listForms(), enabled })
}

/** A listing's enquiry form(s) — its off-market campaign. */
export function useListingForms(dealId?: string) {
  return useQuery({ queryKey: ['forms', 'listing', dealId], queryFn: () => listForms(dealId), enabled: !!dealId })
}

export function useForm(id?: string) {
  return useQuery({ queryKey: ['forms', 'one', id], queryFn: () => getForm(id as string), enabled: !!id })
}

export function useFormSubmissions(formId: string | null) {
  return useQuery({
    queryKey: ['form-submissions', formId],
    queryFn: () => listSubmissions(formId as string),
    enabled: !!formId,
  })
}

export function useCreateForm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: FormInput) => createForm(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  })
}

export function useUpdateForm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: FormInput }) => updateForm(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  })
}

export function useDeleteForm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteForm(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  })
}
